/**
 * Runtime boundaries shared by the browser fallback and the native Mac shell.
 *
 * The website serves its client and API from one origin, so `/api/...` is the
 * right address there. The Mac app embeds the client inside its own WebView and
 * talks to the hosted Sermon Space API, supplied at build time. Keeping that
 * difference here prevents desktop conditionals leaking through every hook.
 */

export const isDesktopApp =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

/** Second-display presenter window, identified before React boots menus. */
export function isPresenterSurface(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("surface") === "presenter") return true;
  return window.location.pathname === "/present";
}

const desktopApiBase = String(
  import.meta.env.VITE_API_BASE_URL || (isDesktopApp ? "https://sermonspace.app" : ""),
)
  .trim()
  .replace(/\/+$/, "");

export function resolveApiUrl(endpoint: string): string {
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  const path = endpoint.startsWith("/api")
    ? endpoint
    : `/api${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
  return desktopApiBase ? `${desktopApiBase}${path}` : path;
}

/**
 * Use the operating system's HTTP stack inside the Mac app.
 *
 * The browser fallback remains same-origin and keeps ordinary `fetch`. Native
 * requests are capability-scoped to sermonspace.app by Tauri, avoid WebView
 * CORS preflights, and still carry the user's Supabase bearer token.
 */
export async function appFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (isDesktopApp) {
    const { fetch: nativeFetch } = await import("@tauri-apps/plugin-http");
    return nativeFetch(input, init);
  }
  return window.fetch(input, init);
}

export function configuredDesktopApi(): boolean {
  return !isDesktopApp || Boolean(desktopApiBase);
}
